import React, { useState } from 'react';
import { SPMFigureMeta } from '../data/ipccData';
import { OriginalIPCCFigureDiagram } from './OriginalIPCCFigureDiagram';
import { BookOpen, CheckCircle2, ShieldCheck, Music, Image as ImageIcon, ExternalLink, ChevronDown, ChevronUp, Info } from 'lucide-react';

interface FigureDescriptionPanelProps {
  meta: SPMFigureMeta;
}

export const FigureDescriptionPanel: React.FC<FigureDescriptionPanelProps> = ({ meta }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [showOriginalDiagram, setShowOriginalDiagram] = useState<boolean>(false);

  return (
    <div className="glass-panel p-6 rounded-3xl border border-slate-800 space-y-6">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-cyan-500/20 text-cyan-400">
            <BookOpen className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white">Scientific Synopsis & Publication Reference</h3>
            <span className="text-[11px] font-mono text-cyan-400">{meta.number} &bull; {meta.title}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Direct External Link to Official IPCC Report Figure */}
          <a
            href={meta.originalIpccUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-bold bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 transition-all hover:scale-105"
            title={`Open official IPCC ${meta.number} publication page in new tab`}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Open Official IPCC Figure ↗</span>
          </a>

          {/* Toggle Original Diagram Schematic */}
          <button
            onClick={() => setShowOriginalDiagram(!showOriginalDiagram)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-mono font-bold transition-all border ${
              showOriginalDiagram
                ? 'bg-amber-500 text-slate-950 border-amber-400 glow-amber'
                : 'bg-slate-900 text-slate-300 border-slate-700 hover:text-white'
            }`}
          >
            <ImageIcon className="w-4 h-4" />
            <span>{showOriginalDiagram ? 'Hide Original Diagram' : 'View Original Diagram Reference'}</span>
          </button>

          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-xl bg-slate-900 text-slate-400 hover:text-white"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Original IPCC Figure Reference Diagram Overlay */}
      {showOriginalDiagram && (
        <div className="p-5 rounded-2xl bg-slate-950 border border-amber-500/50 space-y-4 animate-fade-in">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-xs font-mono font-bold text-amber-400 flex items-center gap-2">
              <ImageIcon className="w-4 h-4" />
              <span>Original Publication Figure Vector Diagram ({meta.number})</span>
            </span>
            
            <a
              href={meta.originalIpccUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono font-bold text-cyan-400 hover:underline flex items-center gap-1"
            >
              <span>View Official High-Res Figure on IPCC.ch</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {/* SVG Vector Diagram Illustration */}
          <OriginalIPCCFigureDiagram figureId={meta.id} originalIpccUrl={meta.originalIpccUrl} />

          <p className="text-xs text-slate-300 leading-relaxed bg-slate-900/80 p-3 rounded-xl border border-slate-800">
            <strong className="text-white block mb-1">Publication Layout Architecture:</strong>
            {meta.originalFigureLayout}
          </p>
        </div>
      )}

      {/* Main Synopsis Content */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-1 text-xs">
          {/* Scientific Overview & Takeaways */}
          <div className="md:col-span-2 space-y-4">
            <div className="space-y-2">
              <h4 className="font-bold text-slate-200 flex items-center gap-2">
                <Info className="w-4 h-4 text-cyan-400" />
                <span>Executive Summary & Context</span>
              </h4>
              <p className="text-slate-300 leading-relaxed">{meta.description}</p>
            </div>

            <div className="space-y-2">
              <h4 className="font-bold text-slate-200 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>Key Scientific Takeaways</span>
              </h4>
              <ul className="space-y-1.5 list-disc list-inside text-slate-300">
                {meta.keyTakeaways.map((takeaway, idx) => (
                  <li key={idx} className="leading-relaxed">
                    {takeaway}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-3.5 rounded-2xl bg-cyan-950/30 border border-cyan-800/50 space-y-1">
              <h4 className="font-bold text-cyan-300 flex items-center gap-1.5 text-[11px] uppercase tracking-wider">
                <ShieldCheck className="w-4 h-4" />
                <span>Policy Relevance</span>
              </h4>
              <p className="text-slate-300 leading-relaxed">{meta.policyRelevance}</p>
            </div>
          </div>

          {/* Acoustic Sonification Mapping */}
          <div className="p-4 rounded-2xl bg-slate-900/80 border border-slate-800 space-y-3">
            <h4 className="font-bold text-slate-200 flex items-center gap-2 border-b border-slate-800 pb-2">
              <Music className="w-4 h-4 text-purple-400" />
              <span>Acoustic Mapping Guide</span>
            </h4>

            <div className="space-y-2.5 text-[11px]">
              <div>
                <strong className="text-cyan-400 block font-mono">Pitch / Frequency:</strong>
                <span className="text-slate-300">{meta.acousticMapping.pitch}</span>
              </div>

              <div>
                <strong className="text-amber-400 block font-mono">Timbre / Synth Patch:</strong>
                <span className="text-slate-300">{meta.acousticMapping.timbre}</span>
              </div>

              <div>
                <strong className="text-emerald-400 block font-mono">Stereo Panning:</strong>
                <span className="text-slate-300">{meta.acousticMapping.pan}</span>
              </div>

              <div>
                <strong className="text-red-400 block font-mono">Amplitude / Intensity:</strong>
                <span className="text-slate-300">{meta.acousticMapping.intensity}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
